import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CalendarDays, CheckCircle2, ExternalLink, Mail, Phone, ShieldAlert, ShieldCheck } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Seo, SITE_URL, courseJsonLd, faqJsonLd } from "../components/Seo";
import { AuroraLight, Overline, PRIMARY, Reveal } from "../components/shared/kursusUi";
import { FactRow, LangNotice, LangSwitcher, money } from "../components/lang/LangBits";
import {
  COURSE_KEYS,
  LANG_COURSE_MAP,
  courseAlternates,
  findLangCourse,
  getLangPack,
  langCoursePath,
} from "../data/lang";
import { getCourseGroupBySlug } from "../data/courseGroups";
import { CONTACT } from "../data/votraLocations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LangCoursePage = ({ lang }) => {
  const { slug } = useParams();
  const pack = getLangPack(lang);
  const found = pack ? findLangCourse(lang, slug) : null;
  const key = found?.key;
  const course = found?.course;
  const group = key ? getCourseGroupBySlug(LANG_COURSE_MAP[key]) : null;
  const [sessions, setSessions] = useState([]);

  const loadSessions = useCallback(async () => {
    if (!group || course?.danishRequired) return;
    try {
      const res = await axios.get(`${API}/course-groups/${group.slug}/sessions`);
      setSessions(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setSessions([]);
    }
  }, [group, course]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  if (!pack || !course || !group) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
        <Seo title="Page not found | Votra" description="The page does not exist." path="/" noindex />
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center py-24 px-4 text-center">
          <h1 className="font-display text-3xl font-medium text-slate-900 mb-3">Page not found</h1>
          <Link to="/kurser" className="font-semibold hover:underline" style={{ color: PRIMARY }}>
            See all Votra courses
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ui = pack.ui;
  const danishPath = `/kurser/${group.slug}`;
  const path = `/${pack.code}/${encodeURIComponent(course.slug)}`;
  const priceDa = group.prices?.da?.standard ?? null;
  const priceForeign = group.prices?.fremmedsprog?.standard ?? null;
  const upcoming = [...sessions]
    .filter((s) => s.date)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .slice(0, 8);

  const instances = upcoming.map((s) => ({
    "@type": "CourseInstance",
    name: `${course.facts.course}, ${s.date}`,
    startDate: s.date,
    ...(s.end_date ? { endDate: s.end_date } : {}),
    courseMode: "onsite",
    ...(s.language ? { inLanguage: s.language } : {}),
    location: {
      "@type": "Place",
      name: s.location || "Votra Danmark ApS",
      address: { "@type": "PostalAddress", addressLocality: s.location || "Ringsted", addressCountry: "DK" },
    },
    ...(typeof s.total_spots === "number" ? { maximumAttendeeCapacity: s.total_spots } : {}),
  }));

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Votra", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: ui.breadcrumb, item: `${SITE_URL}/${pack.code}` },
        { "@type": "ListItem", position: 3, name: course.h1, item: `${SITE_URL}${path}` },
      ],
    },
    courseJsonLd({
      name: course.h1,
      description: course.answer,
      path,
      priceLow: course.danishRequired ? null : (priceForeign || priceDa),
      duration: group.duration,
      languages: [pack.code],
      credential: course.facts.certificate,
      prerequisites: course.facts.entry ? [course.facts.entry] : [],
      teaches: course.sections.map((s) => s.h2),
      courseMode: group.selfStudy ? ["online", "onsite"] : undefined,
      instances: course.danishRequired ? [] : instances,
    }),
    faqJsonLd(course.faq, `${SITE_URL}${path}#faq`),
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
      <div className="votra-grain" aria-hidden />
      <Seo
        title={course.metaTitle}
        description={course.metaDescription}
        path={path}
        lang={pack.htmlLang}
        locale={pack.locale}
        alternates={courseAlternates(SITE_URL, key)}
        jsonLd={jsonLd}
      />
      <div lang="da" dir="ltr">
        <Header />
      </div>
      <LangNotice pack={pack} />

      <div dir={pack.dir} lang={pack.htmlLang} data-testid={`lang-course-${pack.code}`}>
        <section className="hero-light relative overflow-hidden" style={{ backgroundColor: "#F4F6FB" }}>
          <AuroraLight />
          <div className="container mx-auto px-4 md:px-6 relative z-10 py-14 md:py-20">
            <div className="max-w-3xl">
              <Reveal i={1}><Overline light>{ui.breadcrumb}</Overline></Reveal>
              <Reveal i={2}>
                <h1 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight mb-5" data-testid="lang-course-h1">
                  {course.h1}
                </h1>
              </Reveal>
              {course.danishRequired && (
                <Reveal i={3} className="mb-5">
                  <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold bg-amber-400/15 text-amber-200" data-testid="lang-danish-required-badge">
                    <ShieldAlert className="h-4 w-4" /> {ui.danishRequiredBadge}
                  </span>
                </Reveal>
              )}
              <Reveal i={4}>
                <p className="text-base md:text-lg text-slate-300 leading-relaxed">{course.short}</p>
              </Reveal>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 md:px-6 py-12 md:py-16 flex-1">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Kort svar — AI-svarblokken */}
            <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="lang-answer">
              <Overline>{ui.answerHeading}</Overline>
              <p className="mt-3 text-slate-700 leading-relaxed text-[15px] md:text-base">{course.answer}</p>
            </Reveal>

            {/* Faktaboks */}
            <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="lang-factbox">
              <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="h-6 w-6" style={{ color: PRIMARY }} /> {ui.facts.heading}
              </h2>
              <dl>
                <FactRow label={ui.facts.course} testid="lang-fact-course">{course.facts.course}</FactRow>
                <FactRow label={ui.facts.provider}>{CONTACT.legalName} ({CONTACT.brandName})</FactRow>
                <FactRow label={ui.facts.delivery} testid="lang-fact-delivery">{course.facts.delivery}</FactRow>
                <FactRow label={ui.facts.duration}>{course.facts.duration}</FactRow>
                {!course.danishRequired && priceDa && (
                  <FactRow label={ui.facts.priceDa} testid="lang-fact-price-da">
                    {money(priceDa, pack.numLocale)}
                  </FactRow>
                )}
                {!course.danishRequired && priceForeign && (
                  <FactRow label={ui.facts.priceForeign} testid="lang-fact-price-foreign">
                    {money(priceForeign, pack.numLocale)}
                  </FactRow>
                )}
                {!course.danishRequired && group.amuPrice && (
                  <FactRow label={ui.facts.priceAmu}>{money(group.amuPrice, pack.numLocale)}</FactRow>
                )}
                <FactRow label={ui.facts.venue}>{course.facts.venue}</FactRow>
                <FactRow label={ui.facts.certificate}>{course.facts.certificate}</FactRow>
                <FactRow label={ui.facts.entry}>{course.facts.entry}</FactRow>
                <FactRow label={ui.facts.authority}>{course.facts.authority}</FactRow>
              </dl>
            </Reveal>

            {/* Indhold */}
            {course.sections.map((sec, i) => (
              <Reveal key={sec.h2} i={i} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid={`lang-section-${i}`}>
                <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-4">{sec.h2}</h2>
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  {sec.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </Reveal>
            ))}

            {/* Danskkrav → FVU */}
            {course.danishRequired && (
              <Reveal className="rounded-2xl border border-amber-200 bg-amber-50 p-6 sm:p-8" data-testid="lang-fvu-block">
                <h2 className="font-display text-2xl font-medium text-amber-900 mb-3 flex items-center gap-2">
                  <ShieldAlert className="h-6 w-6" /> {ui.danishRequiredBadge}
                </h2>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Link
                    to="/fvu"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-white"
                    style={{ backgroundColor: PRIMARY }}
                    data-testid="lang-fvu-cta"
                  >
                    {ui.fvuCta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {COURSE_KEYS.filter((k) => k !== key && !pack.courses[k]?.danishRequired).map((k) => (
                    <Link
                      key={k}
                      to={langCoursePath(pack.code, k)}
                      className="group flex items-start gap-3 rounded-xl border border-amber-200 bg-white p-4 hover:border-amber-400 transition-colors"
                      data-testid={`lang-alt-course-${k}`}
                    >
                      <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" style={{ color: PRIMARY }} />
                      <span>
                        <span className="font-semibold text-slate-900 block">{pack.courses[k].facts.course}</span>
                        <span className="text-sm text-slate-500">{pack.courses[k].short}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </Reveal>
            )}

            {/* Kommende datoer */}
            {!course.danishRequired && (
              <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="lang-dates">
                <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <CalendarDays className="h-6 w-6" style={{ color: PRIMARY }} />
                  {group.selfStudy ? ui.dates.headingExam : ui.dates.headingCourse}
                </h2>
                {upcoming.length === 0 ? (
                  <p className="text-slate-500" data-testid="lang-dates-empty">{ui.dates.empty}</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {upcoming.map((s) => {
                      const left = typeof s.total_spots === "number" && typeof s.booked_spots === "number"
                        ? Math.max(0, s.total_spots - s.booked_spots)
                        : null;
                      return (
                        <li key={s.id} className="py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-testid={`lang-date-${s.id}`}>
                          <span className="font-semibold text-slate-900">{s.date}</span>
                          <span className="text-sm text-slate-500">{s.location}</span>
                          <span className="text-sm text-slate-500">
                            {ui.dates.language}: {ui.dates.languages[s.language] || s.languageName}
                          </span>
                          <span className="text-sm font-medium" style={{ color: left === 0 ? "#b45309" : PRIMARY }}>
                            {left === 0 ? ui.dates.full : left !== null ? `${left} ${ui.dates.seats}` : ""}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <Link
                  to={`/kalender?sprog=${pack.code}&kursus=${group.slug}`}
                  className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold hover:underline"
                  style={{ color: PRIMARY }}
                  data-testid="lang-calendar-link"
                >
                  {ui.dates.allInLanguage} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Reveal>
            )}

            {/* FAQ */}
            <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="lang-faq">
              <h2 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-4">{ui.faqHeading}</h2>
              <div className="divide-y divide-slate-200 border-t border-slate-200">
                {course.faq.map((f, i) => (
                  <div key={i} className="py-5" data-testid={`lang-faq-${i}`}>
                    <h3 className="text-base font-semibold text-slate-900 mb-1.5">{f.q}</h3>
                    <p className="text-slate-600 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Kilder */}
            <Reveal className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="lang-sources">
              <h2 className="font-display text-xl md:text-2xl font-medium text-slate-900 mb-3">{ui.sourcesHeading}</h2>
              <ul className="space-y-2">
                {course.sources.map((s) => (
                  <li key={s.url}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm hover:underline"
                      style={{ color: PRIMARY }}
                    >
                      {s.label} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </li>
                ))}
              </ul>
            </Reveal>

            {/* CTA */}
            <Reveal className="rounded-2xl p-6 sm:p-8 text-white" style={{ background: "linear-gradient(120deg, #0A192F, #123A6B)" }} data-testid="lang-cta">
              <h2 className="font-display text-2xl md:text-3xl font-medium mb-2">{ui.cta.heading}</h2>
              <p className="text-slate-300 leading-relaxed">{ui.cta.text}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={`tel:${CONTACT.phone}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white"
                  style={{ backgroundColor: PRIMARY }}
                  data-testid="lang-cta-call"
                >
                  <Phone className="h-4 w-4" /> {ui.cta.call}
                </a>
                {!course.danishRequired && (
                  <Link
                    to="/tilmeldingsblanket"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border border-white/25 bg-white/5 hover:bg-white/10 transition-colors"
                    data-testid="lang-cta-signup"
                  >
                    {ui.cta.signUpForm} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold border border-white/25 bg-white/5 hover:bg-white/10 transition-colors"
                  data-testid="lang-cta-email"
                >
                  <Mail className="h-4 w-4" /> {ui.cta.email}
                </a>
              </div>
              <Link
                to={danishPath}
                className="inline-flex items-center gap-2 mt-5 text-sm text-slate-300 hover:text-white transition-colors"
                data-testid="lang-danish-page-link"
              >
                {ui.cta.danishPage} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Reveal>

            <div className="pt-2">
              <LangSwitcher pack={pack} courseKey={key} danishPath={danishPath} danishLabel="Dansk" />
            </div>
          </div>
        </div>
      </div>

      <div lang="da" dir="ltr">
        <Footer />
      </div>
    </div>
  );
};

export default LangCoursePage;
